import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

type UseEditableCellProps = {
  originalValue: string;
  onExitEditMode: (rowId: string, newValue?: string) => void;
  rowId: string;
};

export type UseEditableCellReturn = {
  cellText: string;
  setCellText: Dispatch<SetStateAction<string>>;
  inputRef: RefObject<HTMLInputElement>;
  containerRef: RefObject<HTMLDivElement>;
  isEditing: boolean;
  enterEditMode: () => void;
  exitEditMode: (shouldSave?: boolean) => void;
};

const useEditableCell = ({ originalValue, onExitEditMode, rowId }: UseEditableCellProps): UseEditableCellReturn => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [cellText, setCellText] = useState<string>(originalValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing) {
      // Focus the input after a slight delay to ensure it's rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      // Reset cellText when exiting edit mode without changes
      setCellText(originalValue);
    }
  }, [isEditing, originalValue, inputRef.current]);

  const enterEditMode = useCallback(() => {
    setIsEditing(true);
  }, []);

  const exitEditMode = useCallback(
    (shouldSave = true) => {
      setIsEditing(false);

      if (shouldSave && cellText !== originalValue) {
        onExitEditMode(rowId, cellText); // Save the new value
      } else {
        onExitEditMode(rowId); // Exit without saving
      }
    },
    [onExitEditMode, cellText, originalValue, rowId],
  );

  return {
    cellText,
    setCellText,
    inputRef,
    containerRef,
    isEditing,
    enterEditMode,
    exitEditMode,
  };
};

export default useEditableCell;
